import {
  CRITICALITIES,
  CRITICALITY_STATES,
  CRITICALITY_TYPES,
  ECONOMIC_ZONES,
} from "@/types/FilterConstants";
import type { IPType } from "@/api/wai/wai3Api";
import type { Filter, FilterElement, FilterItem } from "@/types/SuperFilter";

type CreateFilterElementOptions = {
  sortByCount?: boolean;
  singleSelect?: boolean;
};

export const createFilterElement = (
  componentKey: any,
  componentLabel: string,
  options: FilterItem[],
  { sortByCount = false, singleSelect = false }: CreateFilterElementOptions = {},
): FilterElement => ({
  item: { key: componentKey, label: componentLabel },
  options: options.map((opt) => ({ item: opt })),
  sortByCount,
  singleSelect,
});

export const criticalityStatesFilterElement: FilterElement = createFilterElement(
  "state",
  "Tilstand",
  CRITICALITY_STATES.map((type) => ({ key: type.value, label: type.label })),
);

export const drCriticalitiesFilterElement: FilterElement = createFilterElement(
  "drCriticality",
  "ETLS-kritikalitet",
  CRITICALITIES.map((type) => ({ key: type.value, label: type.label })),
);

export const hms1CriticalitiesFilterElement: FilterElement = createFilterElement(
  "hms1Criticality",
  "HMS1-kritikalitet",
  CRITICALITIES.map((type) => ({ key: type.value, label: type.label })),
);

export const hms3CriticalitiesFilterElement: FilterElement = createFilterElement(
  "hms3Criticality",
  "HMS3-kritikalitet",
  CRITICALITIES.map((type) => ({ key: type.value, label: type.label })),
);

export const economicZonesFilterElement: FilterElement = createFilterElement(
  "zone",
  "Økonomisk sone",
  ECONOMIC_ZONES.map((type) => ({ key: type.value, label: type.label })),
);

export const onlyDeviationsFilterElement: FilterElement = createFilterElement(
  "onlyDeviations",
  "Kun strategiavvik",
  [{ key: true, label: "Ja" }],
);

export const createIpTypeFilterElement = (ips: IPType[]): FilterElement =>
  createFilterElement(
    "ipType",
    "Kontrollpunkt",
    ips.map((ip) => ({ key: ip.ip_code, label: ip.description ?? "" })),
    { sortByCount: true },
  );

export const createAssetGroupFilterElement = (
  assetGroups: { name: string }[],
  label: string,
): FilterElement =>
  createFilterElement(
    "assetGroups",
    label,
    assetGroups.map((ag) => ({ key: ag.name, label: ag.name })),
    { sortByCount: true },
  );

export const criticalityTypesFilterElement: FilterElement = createFilterElement(
  "criticalityType",
  "Kritikalitetstype",
  CRITICALITY_TYPES.map((type) => ({ key: type.value, label: type.label })),
  { singleSelect: true },
);

const findItemByKey = (options: FilterItem[], key: string): FilterItem => {
  const option = options.find((opt) => opt.key === key || String(opt.key) === key);
  if (!option) {
    console.warn(
      `Option with key '${key}' was not found in the provided options. Falling back to key as label.`,
    );
    return { key: key, label: key };
  }
  return option;
};

const isFilterItem = (item: any): item is FilterItem => {
  return typeof item === "object" && "key" in item && "label" in item;
};

export const isArrayOfItems = (items: any): items is FilterItem[] => {
  return (
    Array.isArray(items) &&
    items.every((item) => item && typeof item === "object" && "key" in item && "label" in item)
  );
};

const mapItemToFilterItem = (options: FilterItem[], option: any | any[]): FilterItem[] => {
  if (isFilterItem(option)) {
    return [option];
  }
  if (isArrayOfItems(option)) {
    return option;
  }
  if (Array.isArray(option)) {
    return option.map((opt) => findItemByKey(options, opt));
  }
  return [findItemByKey(options, option)];
};

export const mapToFilter = <T,>(
  fe: FilterElement,
  value: T | T[] | FilterItem | FilterItem[],
): Filter => {
  if (!fe || !fe.item) {
    throw new Error("Invalid FilterElement provided.");
  }

  const filterElement = fe.item;

  // Handle empty array (for filter removal)
  if (Array.isArray(value) && value.length === 0) {
    return {
      component: filterElement,
      values: [],
    };
  }

  if (isFilterItem(value)) {
    return {
      component: filterElement,
      values: [value],
    };
  }

  if (isArrayOfItems(value)) {
    return {
      component: filterElement,
      values: value,
    };
  }

  const options = fe.options?.map((o) => o.item) || [];
  const filterOption = mapItemToFilterItem(options, value);
  return {
    component: filterElement,
    values: filterOption,
  };
};

const areItemArraysEqual = (opt1: FilterItem[], opt2: FilterItem[]): boolean => {
  if (opt1.length !== opt2.length) {
    return false;
  }

  const keys1 = opt1.map((o) => o.key).sort();
  const keys2 = opt2.map((o) => o.key).sort();

  return keys1.every((key, index) => key === keys2[index]);
};

const areItemsEqual = (i1: FilterItem, i2: FilterItem): boolean => {
  return i1.key === i2.key;
};

export const findEqualFilter = (filters: Filter[], component: FilterItem): Filter | undefined => {
  return filters.find((filter) => areItemsEqual(filter.component, component));
};

export const isEqualFilters = (f1: Filter, f2: Filter): boolean => {
  return areItemsEqual(f1.component, f2.component) && areItemArraysEqual(f1.values, f2.values);
};

export const getFilterElementByComponent = (
  filterElements: FilterElement[],
  component: FilterItem,
): FilterElement => {
  const filterElement = filterElements.find((fe) => fe.item.key === component.key);
  if (!filterElement) {
    throw new Error(`FilterElement not found for key: ${component.key}`);
  }
  return filterElement;
};
